// Detail-page document picker. Drives the three-step flow per file:
//   1. POST /presentations/{id}/documents/sign-upload → presigned URL
//   2. PUT the bytes directly to the presigned URL
//   3. POST /presentations/{id}/documents → commit (returns row HTML)
// Successful add appends the server-rendered row in-place; remove
// drops the row in-place. No reload — keeps scroll + iframe playback
// intact.

import { onceGuard } from "./bind_once.js";
import { request, signAndPut } from "./presigned_upload.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-presentation-documents]";
const FALLBACK_ATTACH_ERROR =
  "Could not attach the document. Please try again.";
const FALLBACK_REMOVE_ERROR =
  "Could not remove the document. Please try again.";

export function initPresentationDocuments(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(el) {
  if (!firstBind(el)) return;

  const presentationID = el.dataset.presentationId;
  if (!presentationID) return;

  const input = el.querySelector("[data-presentation-documents-input]");
  const list = el.querySelector("[data-presentation-documents-list]");
  const empty = el.querySelector("[data-presentation-documents-empty]");
  const status = el.querySelector("[data-presentation-documents-status]");
  const errorEl = el.querySelector("[data-presentation-documents-error]");

  const signURL = `/presentations/${presentationID}/documents/sign-upload`;
  const commitURL = `/presentations/${presentationID}/documents`;

  if (input) {
    input.addEventListener("change", async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;

      hideError(errorEl);
      input.disabled = true;

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setStatus(
            status,
            `Uploading ${file.name} (${i + 1} of ${files.length})…`,
          );
          const rowHTML = await uploadOne(file, signURL, commitURL);
          if (list) list.insertAdjacentHTML("beforeend", rowHTML);
          toggleEmpty(empty, list);
        }
        hideStatus(status);
      } catch (err) {
        console.error("presentation document upload failed", err);
        showError(errorEl, err.userMessage || FALLBACK_ATTACH_ERROR);
        hideStatus(status);
      } finally {
        input.disabled = false;
        input.value = "";
      }
    });
  }

  el.addEventListener("click", async (event) => {
    const btn = event.target.closest("[data-presentation-document-remove]");
    if (!btn || !el.contains(btn)) return;
    const docID = btn.dataset.presentationDocumentRemove;
    if (!docID) return;

    btn.disabled = true;
    hideError(errorEl);
    try {
      await request(`${commitURL}/${docID}`, { method: "DELETE" });
      const row = btn.closest(`[data-presentation-document-id="${docID}"]`);
      if (row) row.remove();
      toggleEmpty(empty, list);
    } catch (err) {
      console.error("presentation document removal failed", err);
      btn.disabled = false;
      showError(errorEl, err.userMessage || FALLBACK_REMOVE_ERROR);
    }
  });
}

async function uploadOne(file, signURL, commitURL) {
  const signed = await signAndPut(signURL, file);

  const committed = await request(commitURL, {
    fields: { key: signed.key, title: titleFromFilename(file.name) },
    accept: "text/html",
  });

  return committed.text();
}

function toggleEmpty(empty, list) {
  if (!empty || !list) return;
  const hasRows =
    list.querySelectorAll("[data-presentation-document-id]").length > 0;
  empty.classList.toggle("hidden", hasRows);
}

function titleFromFilename(name) {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name).slice(0, 200);
}

function setStatus(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

function hideStatus(el) {
  if (el) el.classList.add("hidden");
}

function showError(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

function hideError(el) {
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}
